/**
 * Processing Step Component
 * Shows order processing status with live updates.
 * Restyled with NowRamp theme tokens.
 */

import { useEffect } from 'react';
import { useRamp } from '../context/RampContext';
import { Spinner } from './ui/Spinner';
import { useT } from '../i18n';

export interface ProcessingStepProps {
  className?: string;
}

export function ProcessingStep({ className = '' }: ProcessingStepProps) {
  const { orderStatus, orderStatusLoading, goToStep } = useRamp();
  const t = useT();

  useEffect(() => {
    if (!orderStatus) return;
    if (orderStatus.status === 'completed') {
      goToStep('complete');
    } else if (['failed', 'cancelled', 'expired', 'refunded'].includes(orderStatus.status)) {
      goToStep('error');
    }
  }, [orderStatus?.status, goToStep]);

  const getStatusMessage = () => {
    if (!orderStatus) return t('processing.default');
    switch (orderStatus.status) {
      case 'pending':
        return t('processing.pending');
      case 'processing':
        return t('processing.active');
      default:
        return t('processing.generic');
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center px-8 py-12 text-center ${className}`}>
      <div className="mb-6" style={{ animation: 'processingPulse 1500ms ease-in-out infinite' }}>
        <Spinner size={64} />
      </div>

      <h2 className="text-[20px] font-semibold mb-3" style={{ color: 'var(--t-text)' }}>
        {getStatusMessage()}
      </h2>

      {orderStatusLoading && (
        <p className="text-[13px] mb-4" style={{ color: 'var(--t-text-muted)' }}>{t('processing.checkingStatus')}</p>
      )}

      {orderStatus && (
        <div className="w-full mt-6 text-left" style={{ background: 'var(--t-surface)', borderRadius: 'var(--t-card-radius, 20px)', padding: 16 }}>
          <DetailRow label={t('processing.orderId')} value={orderStatus.orderId} mono />
          <DetailRow label={t('processing.provider')} value={orderStatus.gateway} />
          <DetailRow
            label={t('processing.amount')}
            value={`${orderStatus.fiatAmount} ${orderStatus.fiatCurrency}`}
          />
          <DetailRow
            label={t('processing.receiving')}
            value={`${orderStatus.cryptoAmount || '...'} ${orderStatus.cryptoCurrency}`}
          />
          <DetailRow label={t('processing.network')} value={orderStatus.network} />
          <DetailRow
            label={t('processing.toAddress')}
            value={`${orderStatus.walletAddress.slice(0, 10)}...${orderStatus.walletAddress.slice(-8)}`}
            mono
          />
        </div>
      )}

      <div className="mt-6">
        <p className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>
          {t('processing.usuallyMinutes')}
        </p>
        <p className="text-[12px] mt-1" style={{ color: 'var(--t-text-muted)' }}>
          {t('processing.safeToClose')}
        </p>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      className="flex justify-between items-center py-2"
      style={{ borderBottom: '1px solid var(--t-divider)' }}
    >
      <span className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>{label}</span>
      <span
        className={`text-[13px] font-medium ${mono ? 'font-mono' : ''}`}
        style={{ color: 'var(--t-text)' }}
      >
        {value}
      </span>
    </div>
  );
}
